import { useState, useEffect, useRef } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { AlertCircle, Globe, Sparkles, Loader2, Search, ChevronDown, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';

import { formatLabel } from '@/lib/format-labels';
// SIC code categories with sub-categories from Companies House
const industryCategories = [
  {
    category: 'Agriculture, Forestry and Fishing',
    subcategories: ['Crop Production', 'Animal Production', 'Forestry & Logging', 'Fishing & Aquaculture']
  },
  {
    category: 'Mining and Quarrying',
    subcategories: ['Coal Mining', 'Oil & Gas Extraction', 'Metal Ores Mining', 'Quarrying']
  },
  {
    category: 'Manufacturing',
    subcategories: ['Food & Beverages', 'Textiles & Clothing', 'Pharmaceuticals', 'Electronics', 'Machinery', 'Motor Vehicles', 'Furniture']
  },
  {
    category: 'Electricity, Gas, Steam and Air Conditioning Supply',
    subcategories: ['Electric Power Generation', 'Gas Distribution', 'Steam & Air Conditioning']
  },
  {
    category: 'Water Supply, Sewerage, Waste Management',
    subcategories: ['Water Collection & Treatment', 'Sewerage', 'Waste Collection', 'Recycling']
  },
  {
    category: 'Construction',
    subcategories: ['Building Construction', 'Civil Engineering', 'Electrical Installation', 'Plumbing', 'Painting & Glazing']
  },
  {
    category: 'Wholesale and Retail Trade',
    subcategories: ['Wholesale', 'Retail Trade', 'Motor Vehicle Sales', 'E-commerce']
  },
  {
    category: 'Transportation and Storage',
    subcategories: ['Rail Transport', 'Road Freight', 'Sea & Coastal Transport', 'Air Transport', 'Warehousing', 'Postal & Courier']
  },
  {
    category: 'Accommodation and Food Service Activities',
    subcategories: ['Hotels & Resorts', 'Holiday Accommodation', 'Restaurants', 'Catering', 'Bars & Pubs']
  },
  {
    category: 'Information and Communication',
    subcategories: ['Publishing', 'Film & Video Production', 'Broadcasting', 'Telecommunications', 'Software Development', 'IT Services']
  },
  {
    category: 'Financial and Insurance Activities',
    subcategories: ['Banking', 'Investment Banking', 'Insurance', 'Pension Funds', 'Fund Management', 'Financial Advisory']
  },
  {
    category: 'Real Estate Activities',
    subcategories: ['Property Development', 'Property Management', 'Estate Agents', 'Property Valuation']
  },
  {
    category: 'Professional, Scientific and Technical Activities',
    subcategories: ['Legal Services', 'Accounting & Auditing', 'Management Consulting', 'Architecture', 'Engineering', 'Research & Development', 'Advertising']
  },
  {
    category: 'Administrative and Support Service Activities',
    subcategories: ['Recruitment', 'Security Services', 'Cleaning Services', 'Call Centres', 'Travel Agencies']
  },
  {
    category: 'Public Administration and Defence',
    subcategories: ['Government Administration', 'Defence Activities', 'Public Order & Safety', 'Social Security']
  },
  {
    category: 'Education',
    subcategories: ['Primary Education', 'Secondary Education', 'Higher Education', 'Vocational Training', 'Tutoring']
  },
  {
    category: 'Human Health and Social Work Activities',
    subcategories: ['Hospitals', 'Medical Practice', 'Dental Practice', 'Nursing Care', 'Social Work', 'Childcare']
  },
  {
    category: 'Arts, Entertainment and Recreation',
    subcategories: ['Performing Arts', 'Museums & Galleries', 'Sports Activities', 'Gambling', 'Amusement Parks']
  },
  {
    category: 'Other Service Activities',
    subcategories: ['Membership Organisations', 'Repair Services', 'Personal Services', 'Funeral Services']
  },
];

// Small helper to render one SIC/CAE row with expand-collapse
function SicCodeRow({ code, description }: { code: string; description: string }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW_LENGTH = 140;
  const isLong = description.length > PREVIEW_LENGTH;
  const preview = isLong && !expanded ? description.slice(0, PREVIEW_LENGTH).trim() + '…' : description;
  return (
    <button
      type="button"
      onClick={() => isLong && setExpanded(e => !e)}
      className={`flex items-start gap-3 w-full text-left rounded-lg px-3 py-2.5 border border-border/50 bg-muted/30 transition-colors ${isLong ? 'cursor-pointer hover:bg-muted/60' : 'cursor-default'}`}
    >
      <Badge
        variant="default"
        className="shrink-0 font-mono text-sm px-2.5 py-1 bg-accent text-accent-foreground border-0 min-w-[3.5rem] justify-center mt-0.5"
      >
        {code}
      </Badge>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-foreground leading-relaxed">{preview}</span>
        {isLong && (
          <span className="ml-1.5 text-xs text-accent font-medium">
            {expanded ? '↑ less' : '↓ more'}
          </span>
        )}
      </div>
    </button>
  );
}

export function CorporateBusinessActivityStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  const company = data.corporateCompany;

  const [businessDescription, setBusinessDescription] = useState(company?.businessDescription || '');
  const [websiteUrl, setWebsiteUrl] = useState(company?.websiteUrl || '');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [translatedActivity, setTranslatedActivity] = useState<string>(company?.mainActivityEnglish || '');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isFetchingCode, setIsFetchingCode] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);
  
  // Manual industry selection state
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(company?.manualIndustries || []);
  const [industrySearch, setIndustrySearch] = useState('');
  const [showIndustryList, setShowIndustryList] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasSicCodes = company?.sicCodes && company.sicCodes.length > 0;
  const hasMainActivity = !hasSicCodes && (company?.mainActivity || company?.mainActivityCode);

  // Auto-translate mainActivity to English when component mounts or data changes
  useEffect(() => {
    if (!company?.mainActivity) return;

    // Already translated and different from the raw text — use cached version
    const alreadyTranslated =
      company.mainActivityEnglish &&
      company.mainActivityEnglish !== company.mainActivity;

    if (alreadyTranslated) {
      setTranslatedActivity(company.mainActivityEnglish!);
      return;
    }

    // Fire translation (covers: no cached translation OR cached == original text)
    let cancelled = false;
    const translate = async () => {
      setIsTranslating(true);
      try {
        const { data: fnData, error } = await supabase.functions.invoke('translate-activity', {
          body: {
            text: company.mainActivity,
            code: company.mainActivityCode || '',
          }
        });
        if (cancelled) return;
        if (error) throw error;
        const english = fnData?.translated || company.mainActivity;
        setTranslatedActivity(english);
        // Cache it so it doesn't re-translate on next render
        updateData({ corporateCompany: { ...company, mainActivityEnglish: english } });
      } catch (err) {
        if (!cancelled) setTranslatedActivity(company.mainActivity || '');
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };
    translate();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.mainActivity]);

  // If mainActivityCode is missing, re-fetch using report (connectId optional — edge function uses nif.pt fallback)
  useEffect(() => {
    if (company?.mainActivityCode) return; // already have it
    if (!company?.registrationNumber || !company?.countryCode) return; // nothing to fetch with

    let cancelled = false;
    const refetch = async () => {
      setIsFetchingCode(true);
      try {
        const { data: fnData, error } = await supabase.functions.invoke('creditsafe-company', {
          body: {
            action: 'report',
            connectId: company.connectId || undefined, // optional — edge fn handles missing connectId
            countryCode: company.countryCode,
            regNo: company.registrationNumber,
            vatNo: company.vatNumber || company.registrationNumber,
          }
        });
        if (cancelled || error || !fnData?.success) return;
        const d = fnData.data;
        const code = (d?.mainActivityCode || d?.sicCodes?.[0]?.code || '').trim();
        if (!cancelled && code) {
          updateData({
            corporateCompany: {
              ...company,
              mainActivityCode: code,
              ...(d?.mainActivity && { mainActivity: d.mainActivity }),
              ...(d?.mainActivityEnglish && { mainActivityEnglish: d.mainActivityEnglish }),
              ...(d?.sicCodes?.length && { sicCodes: d.sicCodes }),
              ...(d?.connectId && { connectId: d.connectId }),
            }
          });
        }
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setIsFetchingCode(false);
      }
    };
    refetch();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.registrationNumber, company?.mainActivityCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowIndustryList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter categories and subcategories based on search
  const filteredCategories = industryCategories
    .map(cat => ({
      ...cat,
      subcategories: cat.subcategories.filter(sub =>
        sub.toLowerCase().includes(industrySearch.toLowerCase()) ||
        cat.category.toLowerCase().includes(industrySearch.toLowerCase())
      )
    }))
    .filter(cat => 
      cat.category.toLowerCase().includes(industrySearch.toLowerCase()) ||
      cat.subcategories.length > 0
    );

  // Auto-expand matching category when searching
  useEffect(() => {
    if (industrySearch.length > 0 && filteredCategories.length > 0) {
      const matchingCategory = filteredCategories.find(cat => 
        cat.subcategories.some(sub => 
          sub.toLowerCase().includes(industrySearch.toLowerCase())
        )
      );
      if (matchingCategory) {
        setExpandedCategory(matchingCategory.category);
      } else if (filteredCategories.length === 1) {
        setExpandedCategory(filteredCategories[0].category);
      }
    } else if (industrySearch.length === 0) {
      setExpandedCategory(null);
    }
  }, [industrySearch]);

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground">Company data not found.</p>
        </div>
      </div>
    );
  }

  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }

    setIsAnalyzing(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('analyze-website', {
        body: { websiteUrl: websiteUrl.trim() }
      });

      if (error) {
        console.error('Error analyzing website:', error);
        toast.error('Failed to analyze website. Please try again.');
        return;
      }

      if (result?.success && result?.description) {
        setBusinessDescription(result.description);
        
        // Store branding data (logo, colors) if available
        if (result.branding) {
          updateData({
            companyBranding: result.branding,
            companyLogo: result.branding.logo || null
          });
        }
        
        toast.success('Business description generated from website!');
      } else {
        toast.error(result?.error || 'Could not extract information from website');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to analyze website');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAddIndustry = (industry: string) => {
    if (!selectedIndustries.includes(industry)) {
      setSelectedIndustries([...selectedIndustries, industry]);
    }
    setIndustrySearch('');
    setShowIndustryList(false);
    setExpandedCategory(null);
  };

  const handleRemoveIndustry = (industry: string) => {
    setSelectedIndustries(selectedIndustries.filter(i => i !== industry));
  };

  const handleContinue = () => {
    updateData({
      corporateCompany: {
        ...company,
        businessDescription,
        websiteUrl,
        manualIndustries: selectedIndustries,
      }
    });
    goToNextStep();
  };

  // Form is valid if description is 20+ chars AND (has SIC codes OR has main activity OR has manual industries)
  const hasIndustryInfo = hasSicCodes || hasMainActivity || selectedIndustries.length > 0;
  const isFormValid = businessDescription.trim().length >= 20 && hasIndustryInfo;

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-24 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          {/* Header */}
          <h1 className="text-xl sm:text-2xl font-bold text-center tracking-[0.15em] mb-1">
            BUSINESS ACTIVITY
          </h1>
          <p className="text-muted-foreground text-center text-xs mb-4">
            Registered activities and business description
          </p>

          {/* Business Activity Card — SIC/CAE codes (all of them), or mainActivity fallback */}
          {(hasSicCodes || hasMainActivity) && (
            <div className="rounded-xl border border-border bg-card p-4 mb-3">
              <p className="text-muted-foreground text-sm mb-3">
                {hasSicCodes ? 'Registered Industry Classifications' : 'Primary Business Activity'}
              </p>
              <div className="space-y-3">
                {hasSicCodes
                  ? company.sicCodes.map((sic: { code: string; description: string }, index: number) => (
                      <SicCodeRow key={index} code={sic.code} description={sic.description} />
                    ))
                  : (
                    <div className="flex items-start gap-3">
                      {/* CAE/SIC badge — spinner while fetching, code once loaded */}
                      {isFetchingCode ? (
                        <Badge
                          variant="default"
                          className="shrink-0 font-mono text-sm px-2.5 py-1 bg-accent/50 text-accent-foreground border-0 min-w-[3rem] flex items-center justify-center"
                        >
                          <Loader2 className="w-3 h-3 animate-spin" />
                        </Badge>
                      ) : (company.mainActivityCode && company.mainActivityCode !== 'MAIN') ? (
                        <Badge
                          variant="default"
                          className="shrink-0 font-mono text-sm px-2.5 py-1 bg-accent text-accent-foreground border-0 min-w-[3rem] text-center justify-center"
                        >
                          {company.mainActivityCode}
                        </Badge>
                      ) : null}
                      <div className="flex-1">
                        {isTranslating ? (
                          <span className="text-sm text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Translating to English…
                          </span>
                        ) : (() => {
                          const text = translatedActivity || company.mainActivity || '';
                          const isLong = text.length > 80;
                          const preview = isLong && !activityExpanded ? text.slice(0, 80).trim() + '…' : text;
                          return (
                            <>
                              <span className="text-sm text-foreground leading-relaxed">{preview}</span>
                              {isLong && (
                                <button
                                  onClick={() => setActivityExpanded(e => !e)}
                                  className="ml-1.5 text-xs text-accent underline-offset-2 hover:underline"
                                >
                                  {activityExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )
                }
              </div>
              {hasMainActivity && !hasSicCodes && (
                <p className="text-xs text-muted-foreground mt-3">
                  Please add specific industries below to complete your profile.
                </p>
              )}
            </div>
          )}
          
          {/* Manual Industry Selection - always show when no SIC codes */}
          {!hasSicCodes && (
            <div className="rounded-xl border border-border bg-card p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-muted-foreground text-sm">Business Industries {!hasMainActivity && <span className="text-destructive">*</span>}</p>
                <span className="text-xs text-muted-foreground">{hasMainActivity ? 'Add more (optional)' : 'Select at least one'}</span>
              </div>
              
              {/* Selected industries */}
              {selectedIndustries.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedIndustries.map((industry, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="text-xs py-1.5 px-3 bg-accent/20 text-accent border-accent/30 flex items-center gap-1.5"
                    >
                      {industry}
                      <button
                        onClick={() => handleRemoveIndustry(industry)}
                        className="hover:text-destructive transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Industry search dropdown */}
              <div className="relative" ref={dropdownRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <Input
                  value={industrySearch}
                  onChange={(e) => {
                    setIndustrySearch(e.target.value);
                    setShowIndustryList(true);
                  }}
                  onFocus={() => setShowIndustryList(true)}
                  placeholder="Search and add industries..."
                  className="pl-10 h-10 bg-background border-border text-sm"
                />
                
                {showIndustryList && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((cat) => (
                        <div key={cat.category}>
                          <button
                            onClick={() => setExpandedCategory(expandedCategory === cat.category ? null : cat.category)}
                            className="w-full px-3 py-2 text-left hover:bg-muted/50 text-foreground text-sm font-medium flex items-center justify-between border-b border-border/50"
                          >
                            <span className="truncate">{formatLabel(cat.category)}</span>
                            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${expandedCategory === cat.category ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedCategory === cat.category && (
                            <div className="bg-muted/20">
                              {cat.subcategories.map((sub) => {
                                const fullIndustry = `${cat.category} - ${sub}`;
                                const isSelected = selectedIndustries.includes(fullIndustry);
                                return (
                                  <button
                                    key={sub}
                                    onClick={() => !isSelected && handleAddIndustry(fullIndustry)}
                                    disabled={isSelected}
                                    className={`w-full px-6 py-2 text-left text-xs flex items-center gap-2 ${
                                      isSelected 
                                        ? 'text-muted-foreground cursor-not-allowed' 
                                        : 'hover:bg-accent/20 text-foreground/80'
                                    }`}
                                  >
                                    {!isSelected && <Plus className="w-3 h-3 text-accent" />}
                                    {sub}
                                    {isSelected && <span className="text-accent ml-auto">Added</span>}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-muted-foreground text-sm">No matching industries</div>
                    )}
                  </div>
                )}
              </div>
              
              {!hasMainActivity && (
                <p className="text-xs text-muted-foreground mt-2">
                  No registered industry codes found. Please select the industries your business operates in.
                </p>
              )}
            </div>
          )}

          {/* Website Input Card */}
          <div className="rounded-xl border border-border bg-card p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <p className="text-muted-foreground text-sm">Company Website (optional)</p>
            </div>
            
            <div className="flex gap-2">
              <Input
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="www.yourcompany.com"
                className="flex-1 bg-background border-border text-sm h-10"
                disabled={isAnalyzing}
              />
              <Button
                type="button"
                onClick={handleAnalyzeWebsite}
                disabled={isAnalyzing || !websiteUrl.trim()}
                size="sm"
                className="h-10 px-3"
              >
                {isAnalyzing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-2">
              {isAnalyzing ? 'Analyzing website...' : 'Enter URL and click ✨ to auto-generate description'}
            </p>
          </div>

          {/* Business Description Card */}
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-muted-foreground text-sm mb-2">Business description <span className="text-destructive">*</span></p>
            
            <Textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              placeholder="Describe your company's products, services, and target market..."
              className="min-h-[100px] resize-none bg-background border-border text-sm"
            />
            
            <p className="text-xs text-muted-foreground mt-2">
              {businessDescription.length}/20 characters minimum
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleContinue}
            disabled={!isFormValid || !canActivate}
            className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
